import { Controller } from "@hotwired/stimulus"
import { computePosition, shift, offset, flip, autoUpdate } from "@floating-ui/dom"

export default class extends Controller {
    static targets = ['tooltip']
    static values = {
        position: String
    }

    connect() {
        this.cleanup = null
        this.hovered = false
        this.focused = false
    }

    show(event) {
        if (!this.hasTooltipTarget) return

        if (event?.type === "mouseenter") this.hovered = true
        if (event?.type === "focusin") {
            this.focused = true
            this.describe(event.target)
        }
        if (this.activeTooltip) return

        const reference = this.element
        const tooltip = this.tooltipTarget
        const session = {}

        this.activeTooltip = tooltip
        this.session = session
        tooltip.hidden = false

        this.cleanup = autoUpdate(reference, tooltip, () => {
            const request = {}
            session.request = request
            computePosition(reference, tooltip, {
                placement: (this.positionValue || "top"),
                middleware: [offset(8), flip(), shift()]
            }).then(({ x, y }) => {
                if (this.session !== session || session.request !== request) return

                Object.assign(tooltip.style, {
                    left: `${x}px`,
                    top: `${y}px`
                })
            })
        })
    }

    hide(event) {
        if (event?.type === "mouseleave") {
            this.hovered = false
        } else if (event?.type === "focusout") {
            if (this.element.contains(event.relatedTarget)) return

            this.focused = false
            this.clearDescription()
        } else {
            this.hovered = false
            this.focused = false
        }
        if (this.hovered || this.focused) return

        this.session = null
        if (this.activeTooltip) this.activeTooltip.hidden = true
        this.activeTooltip = null
        this.clearDescription()
        if (this.cleanup) {
            this.cleanup()
            this.cleanup = null
        }
    }

    describe(element) {
        this.clearDescription()
        this.describedElement = element
        this.descriptionId = this.tooltipTarget.id
        const ids = (element.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean)
        if (!ids.includes(this.descriptionId)) ids.push(this.descriptionId)
        element.setAttribute("aria-describedby", ids.join(" "))
    }

    clearDescription() {
        if (!this.describedElement) return

        const element = this.describedElement
        const ids = (element.getAttribute("aria-describedby") || "").split(/\s+/).filter(id => id && id !== this.descriptionId)
        if (ids.length) {
            element.setAttribute("aria-describedby", ids.join(" "))
        } else {
            element.removeAttribute("aria-describedby")
        }
        this.describedElement = null
    }

    disconnect() {
        this.hide()
    }
}
